import { useState, useRef, useCallback, useEffect } from 'react';
import startStockReport from '../../api/cars/startStockReport';
import getStockReportStatus from '../../api/cars/getStockReportStatus';
import downloadStockReport from '../../api/cars/downloadStockReport';
import alert from '../../helpers/alert';

const POLL_INTERVAL_MS = 1000;
const MAX_POLL_FAILURES = 5;

const buildFileName = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `Сток_${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.xlsx`;
};

const saveBlob = (data) => {
  const blob = new Blob([data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = buildFileName();
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const useStockReportJob = () => {
  const [phase, setPhase] = useState('idle');
  const [progress, setProgress] = useState({ processed: 0, total: 0, percent: 0 });
  const [errorMessage, setErrorMessage] = useState('');
  const [doneTotal, setDoneTotal] = useState(0);
  const [doneReportId, setDoneReportId] = useState(null);

  const pollTimerRef = useRef(null);
  const pollFailuresRef = useRef(0);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const failWith = useCallback(
    (message) => {
      stopPolling();
      setErrorMessage(message);
      setPhase('error');
    },
    [stopPolling]
  );

  const handleDone = useCallback(
    async (reportId, total) => {
      stopPolling();

      if (total === 0) {
        setDoneTotal(0);
        setPhase('empty');
        return;
      }

      setPhase('downloading');
      try {
        const response = await downloadStockReport(reportId);
        saveBlob(response.data);
        setDoneReportId(reportId);
        setDoneTotal(total);
        setPhase('success');
      } catch (err) {
        failWith('Отчет сформирован, но скачать файл не удалось. Попробуйте еще раз.');
      }
    },
    [stopPolling, failWith]
  );

  const startPolling = useCallback(
    (reportId) => {
      pollFailuresRef.current = 0;

      const poll = async () => {
        try {
          const res = await getStockReportStatus(reportId);
          pollFailuresRef.current = 0;
          const { status, total, processed, percent, error } = res.data;
          setProgress({ processed, total, percent });

          if (status === 'done') {
            handleDone(reportId, total);
            return;
          }
          if (status === 'error') {
            failWith(error || 'Ошибка при формировании отчета');
            return;
          }
        } catch (err) {
          pollFailuresRef.current += 1;
          if (err?.response?.status === 404) {
            failWith('Отчет не найден на сервере. Попробуйте сформировать заново.');
            return;
          }
          if (pollFailuresRef.current >= MAX_POLL_FAILURES) {
            failWith('Потеряна связь с сервером. Попробуйте сформировать отчет заново.');
            return;
          }
        }
        pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      };

      pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    },
    [failWith, handleDone]
  );

  const start = useCallback(
    async (filters, simulateError = false) => {
      if (phase === 'running') return;

      setPhase('running');
      setProgress({ processed: 0, total: 0, percent: 0 });
      setErrorMessage('');
      setDoneReportId(null);

      try {
        const res = await startStockReport({ ...filters, simulateError });
        if (!res?.data?.reportId) {
          failWith('Сервер не вернул идентификатор отчета. Попробуйте еще раз.');
          return;
        }
        startPolling(res.data.reportId);
      } catch (err) {
        failWith('Не удалось запустить формирование отчета. Попробуйте еще раз.');
      }
    },
    [phase, failWith, startPolling]
  );

  const downloadAgain = useCallback(async () => {
    if (!doneReportId) return;
    try {
      const response = await downloadStockReport(doneReportId);
      saveBlob(response.data);
    } catch (err) {
      alert.error('Файл устарел. Сформируйте отчет заново.');
      setDoneReportId(null);
      setPhase('idle');
    }
  }, [doneReportId]);

  return { phase, progress, errorMessage, doneTotal, start, downloadAgain };
};

export default useStockReportJob;
